import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Leaf, Award, Clock, MapPin } from 'lucide-react';

const Presentation = () => {
  return (
    <div className="page-presentation">
      {/* Hero Section */}
      <section className="presentation-hero">
        <div className="service-hero-pattern" />
        <div className="presentation-hero-content">
          <h1 className="presentation-hero-title">Meya Beauté & Tradition</h1>
          <p className="presentation-hero-subtitle">L'Art du Bien-Être Oriental à Nancy</p>
          <p className="presentation-hero-description">
            Découvrez un havre de paix où tradition et modernité se rencontrent pour 
            vous offrir une expérience de relaxation unique et authentique
          </p>
        </div>
      </section>

      {/* Notre Histoire */}
      <section className="notre-histoire-section">
        <div className="container-main">
          <div className="section-header">
            <h2 className="section-title">Notre Histoire</h2>
            <div className="decorative-line" />
          </div>

          <div className="histoire-content">
            <div className="histoire-text">
              <p className="lead-text">
                <strong>Meya Beauté & Tradition</strong> est né d'une passion pour les rituels 
                de beauté orientaux ancestraux et d'un désir de partager ces trésors de bien-être 
                avec vous.
              </p>
              <p>
                Inspiré par les traditions millénaires du Maghreb et du Moyen-Orient, notre salon 
                vous transporte dans un voyage sensoriel unique. Chaque soin est pensé pour 
                harmoniser le corps et l'esprit, en utilisant des techniques transmises de 
                génération en génération.
              </p>
              <p>
                Dans notre espace chaleureux situé à Vandoeuvre-lès-Nancy, nous avons recréé 
                l'ambiance authentique des hammams traditionnels, où le temps s'arrête et 
                où chaque moment est dédié à votre bien-être absolu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Valeurs */}
      <section className="valeurs-section">
        <div className="container-main">
          <div className="section-header">
            <h2 className="section-title">Nos Valeurs</h2>
            <div className="decorative-line" />
          </div>

          <div className="valeurs-grid">
            <div className="valeur-card">
              <div className="valeur-icon">
                <Leaf size={40} />
              </div>
              <h3>Naturel & Authentique</h3>
              <p>
                Nous utilisons exclusivement des huiles naturelles et des produits 
                traditionnels : huile de nigelle, sésame, dattier, figue de barbarie. 
                Chaque produit est sélectionné pour ses vertus reconnues et sa pureté.
              </p>
            </div>

            <div className="valeur-card">
              <div className="valeur-icon">
                <Heart size={40} />
              </div>
              <h3>Bienveillance & Écoute</h3>
              <p>
                Chaque cliente est unique. Nous prenons le temps de comprendre vos besoins 
                et adaptons nos soins à votre situation : femmes enceintes, jeunes mamans, 
                moments mère-fille... Vous êtes au cœur de notre attention.
              </p>
            </div>

            <div className="valeur-card">
              <div className="valeur-icon">
                <Sparkles size={40} />
              </div>
              <h3>Tradition & Savoir-Faire</h3>
              <p>
                Nos techniques de massage et de soins sont issues de traditions ancestrales 
                orientales. Formées aux gestes traditionnels, nous perpétuons un art de vivre 
                et un savoir-faire authentique.
              </p>
            </div>

            <div className="valeur-card">
              <div className="valeur-icon">
                <Award size={40} />
              </div>
              <h3>Excellence & Qualité</h3>
              <p>
                De la réservation à la fin de votre soin, nous veillons à ce que chaque détail 
                contribue à votre bien-être. Environnement apaisant, produits d'exception, 
                gestes experts : tout est pensé pour votre satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Philosophie */}
      <section className="philosophie-section">
        <div className="container-main">
          <div className="philosophie-content">
            <div className="philosophie-text">
              <h2 className="section-title">Notre Philosophie</h2>
              <div className="decorative-line" style={{ margin: '1.5rem 0' }} />
              <p className="philosophie-lead">
                "Le bien-être n'est pas un luxe, c'est une nécessité"
              </p>
              <p>
                Chez Meya, nous croyons que prendre soin de soi n'est pas une indulgence, 
                mais un besoin essentiel. Dans notre société moderne où tout va vite, 
                s'accorder un moment de pause devient vital.
              </p>
              <p>
                Nos rituels de beauté sont bien plus que de simples soins : ce sont des 
                moments de reconnexion avec soi-même, des parenthèses enchantées où vous 
                pouvez lâcher prise et vous ressourcer en profondeur.
              </p>
              <p>
                Que vous veniez pour un massage revitalisant, un soin des mains et des pieds, 
                ou un moment de complicité mère-fille, vous repartirez apaisée, régénérée 
                et sublimée naturellement.
              </p>
            </div>

            <div className="philosophie-highlights">
              <div className="highlight-item">
                <Sparkles size={24} />
                <div>
                  <h4>Rituels Personnalisés</h4>
                  <p>Chaque soin est adapté à vos besoins spécifiques</p>
                </div>
              </div>
              <div className="highlight-item">
                <Heart size={24} />
                <div>
                  <h4>Atmosphère Authentique</h4>
                  <p>Un cadre chaleureux inspiré des traditions orientales</p>
                </div>
              </div>
              <div className="highlight-item">
                <Leaf size={24} />
                <div>
                  <h4>Produits Naturels</h4>
                  <p>100% naturels, respectueux de votre peau</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Engagements */}
      <section className="engagements-section">
        <div className="container-main">
          <div className="section-header">
            <h2 className="section-title">Nos Engagements</h2>
            <div className="decorative-line" />
          </div>

          <div className="engagements-grid">
            <div className="engagement-card">
              <div className="engagement-number">01</div>
              <h3>Hygiène Irréprochable</h3>
              <p>
                Protocoles d'hygiène stricts, matériel stérilisé, linge propre à chaque soin. 
                Votre sécurité et votre confort sont notre priorité absolue.
              </p>
            </div>

            <div className="engagement-card">
              <div className="engagement-number">02</div>
              <h3>Respect & Confidentialité</h3>
              <p>
                Un espace intimiste où vous vous sentez en confiance. Nous respectons votre 
                pudeur et garantissons la confidentialité de nos échanges.
              </p>
            </div>

            <div className="engagement-card">
              <div className="engagement-number">03</div>
              <h3>Sur Rendez-vous Uniquement</h3>
              <p>
                Pour vous garantir une attention exclusive, nous travaillons uniquement sur 
                rendez-vous. Chaque cliente bénéficie d'un moment privilégié, sans précipitation.
              </p>
            </div>

            <div className="engagement-card">
              <div className="engagement-number">04</div>
              <h3>Tarifs Transparents</h3>
              <p>
                Nos prix sont affichés clairement et incluent tout ce qui est annoncé. 
                Pas de mauvaise surprise, juste le plaisir de profiter de votre soin.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Informations Pratiques */}
      <section className="infos-pratiques-section">
        <div className="container-main">
          <div className="infos-grid">
            <div className="info-pratique-card">
              <MapPin size={32} />
              <h3>Notre Adresse</h3>
              <p>6 rue du vivarais</p>
              <p>54500 Vandoeuvre-lès-Nancy</p>
            </div>

            <div className="info-pratique-card">
              <Clock size={32} />
              <h3>Horaires</h3>
              <p>Sur rendez-vous uniquement</p>
              <p>Du lundi au dimanche</p>
            </div>

            <div className="info-pratique-card">
              <Phone size={32} />
              <h3>Contact</h3>
              <p>{contactInfo.phone}</p>
              <p>{contactInfo.email}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="presentation-cta">
        <div className="cta-content">
          <h2 className="cta-title">Prête à Vivre l'Expérience Meya ?</h2>
          <p className="cta-text">
            Offrez-vous un moment de détente unique dans notre havre de paix oriental
          </p>
          <div className="cta-buttons">
            <Link to="/reservation" className="btn-primary">
              Réserver un Soin
            </Link>
            <Link to="/contact" className="btn-secondary">
              Nous Contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Fix import
import { Phone } from 'lucide-react';

export default Presentation;
